// English source dictionary (the default and the fallback for every other
// language). Keys are grouped by area with dotted names. {param} placeholders
// are filled by t(). Theme proper nouns (era / planet / enemy names) are NOT
// here: they stay as the game's identity across all languages.

export const en: Record<string, string> = {
  "dawn.era0": "The open steppe",
  "dawn.era1": "The khagans",
  "dawn.era2": "Stone and empire",
  "dawn.era3": "The republic",
  "dawn.era4": "Beyond the sky",
  // common buttons / words
  'common.continue': 'Continue',
  'common.skip': 'Skip',
  'common.next': 'Next',
  'common.finish': 'Done',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.send': 'Send',
  'common.begin': 'Begin',

  // top-left identity dashboard + stats
  'hud.spoils': 'Spoils',
  'hud.skillPts': 'Skill pts',
  'hud.cosmic': 'Cosmic',
  'hud.population': 'Population',
  'hud.buildings': 'Buildings',
  'hud.autoOn': 'Auto-collect: on',
  'hud.autoProgress': 'Auto-collect: {have}/{need} gatherers',
  'hud.nextBuilding': 'Next building',
  'hud.needPop': 'grow population first',
  'hud.ascend': 'Ascend',

  // wave panel
  'wave.next': 'Next wave',
  'wave.waveN': 'Wave {n}',
  'wave.bossN': 'Boss wave ({n})',
  'wave.incoming': 'Wave incoming',
  'wave.bossIncoming': 'Boss incoming',
  'wave.now': 'NOW',
  'wave.yourPower': 'Your power',
  'wave.enemyPower': 'Enemy power',
  'wave.conquer': '{planet} conquest: age {goal}',
  'wave.conquered': '{planet} conquered',

  // era gate (the boss that gates each age advance)
  'gate.progress': '{boss} gathering at the gate: {pct}%',
  'gate.atGate': '{boss} at the gate!',
  'gate.final': 'Final age reached',
  'gate.summonTitle': 'The gate opens',
  'gate.summonSub': '{boss} is massing at the gate. Defeat it to enter the next age; if it breaks through, your line falls.',
  'gate.freeProgress': 'Next age: {pct}%',
  'gate.cleared': 'Gate broken',
  'gate.clearedSub': 'A new age lies ahead',

  // wave result banners
  'banner.repelled': 'Wave repelled',
  'banner.overrun': 'Village stormed',
  'banner.onHorizon': '{enemy} on the horizon',
  'banner.bossApproaches': 'The boss marches on the gate',
  'banner.scattered': '{enemy} scattered',
  'banner.brokeThrough': '{enemy} broke through',

  // milestones / toasts
  'milestone.autoTitle': 'Auto-collection unlocked',
  'milestone.autoSub': 'Your people now gather resources on their own.',
  'milestone.settlementNow': 'Your settlement is now a {name}',
  'milestone.settlementGrows': 'Your settlement grows',

  // intro / story
  'intro.begin': 'Begin',
  'intro.tagline': 'From the dust of the steppe to dominion over the sky.',
  'intro.story':
    'Under the eternal blue sky, a handful of tents on the steppe. You do not rule the people; you are the will of heaven that guides them. Rise age by age, return stronger from your ashes when you fall, and open to the stars until the sky is no longer a ceiling but a homeland to return to.',

  // what's new
  'news.title': "What's new",
  'news.sub': 'What changed since your last visit (v{version})',
  'news.all': 'All updates',

  // tutorial
  'tut.s1.title': 'Welcome',
  'tut.s1.text':
    'These are your people. Tap where you want to gather; the yellow circle settles there and works the nearby trees, rocks and ore. Tap somewhere else to move it (on desktop it follows your mouse).',
  'tut.s2.title': 'Resources',
  'tut.s2.text': 'The wood, stone, ore and knowledge you gather pile up here.',
  'tut.s3.title': 'Your settlement',
  'tut.s3.text':
    'Your people build on their own with these resources. Population, building count and what the next building needs are shown here.',
  'tut.s4.title': 'Waves',
  'tut.s4.text':
    'Raiders come at intervals. If your power is enough, you drive them off; if a wave breaks through, your line falls and you rise again with wisdom.',
  'tut.s5.title': 'The rest is yours',
  'tut.s5.text':
    'Later ages are each guarded by a boss at the gate: defeat it to claim an artifact and pick a direction (economy, army, knowledge). 🔬 technology, 👤 account and feedback. Have fun!',

  // account panel
  'account.title': 'Account',
  'account.feedbackLabel': 'Feedback',
  'account.feedbackPlaceholder': 'What do you think? Bug, idea, feeling... it all helps.',
  'account.dangerZone': 'Danger zone',
  'account.resetBtn': 'Wipe all progress (start over)',
  'account.resetConfirm':
    'Are you sure? Skill tree, cosmic, planets and the current run all get wiped. This cannot be undone.',
  'account.resetYes': 'Yes, wipe everything',
  'account.resetNo': 'Cancel',
  'account.replayTutorial': 'Show the guide again',
  'account.logout': 'Sign out',
  'account.signIn': 'Sign in to save my progress',
  'account.guest': 'Playing as a guest. Your progress is on this device only.',
  'account.language': 'Language',
  'account.supportLabel': 'Support',
  'account.supportBtn': 'Support this game',

  // feedback nudge
  'nudge.early': 'How is it going? A quick note helps a lot 🙏',
  'nudge.later': "You made it this far! Mind sharing what you think?",
  'nudge.cta': 'Feedback',

  // login
  'login.title': 'Sign in',
  'login.sub': 'Sign in to keep your progress across devices. You can skip this and sign in any time from 👤.',
  'login.guest': 'Play now, sign in later',
  'login.emailPlaceholder': 'your@email.com',
  'login.sendCode': 'Send code',
  'login.codePlaceholder': '6-digit code',
  'login.verify': 'Verify',
  'login.sending': 'Sending...',
  'login.codeSent': 'A code was sent to your email.',
  'login.verifying': 'Verifying...',
  'login.errGeneric': 'Something went wrong, please try again.',
  'login.errCode': 'The code is wrong or expired.',

  // death / skill tree
  'death.title': 'Your line has fallen',
  // Phrased as "Waves held: N" rather than "held N waves" because the most
  // common death of all is at exactly one wave, and "held 1 waves" was the
  // first sentence most players ever read here.
  'death.lede':
    'You reached Age {age}. Waves held: {waves}. Spend your hard-won wisdom, then rise again.',
  'death.bossLede':
    'The era boss {boss} broke your gate at Age {age}. Waves held: {waves}. Put your new points into defense before you face it again.',
  'death.wavesHeld': 'Waves held',
  'death.ageReached': 'Age reached',
  'death.skillPoints': 'Skill points',
  'death.restart': 'Rise again',
  'death.ascendTo': '🚀 Ascend to {planet} (+{gain} cosmic)',

  // offline report
  'offline.title': 'While you were away',
  'offline.sub': 'Your people kept working for {time}.',
  'offline.collect': 'Collect',

  // card picker
  'cards.title': 'An age turns',
  'cards.sub': 'Choose one direction for your people.',

  'cat.economy': 'Economy',
  'cat.military': 'Military',
  'cat.knowledge': 'Knowledge',
  'lab.active': 'Active',
  'lab.cost': 'Cost',
  'lab.title': 'Laboratory',
  'lab.spoilsWord': 'Spoils',
  'lab.techs': 'Technologies (spoils)',
  'lab.artifacts': 'Artifacts (activate with resources)',
  'lab.artifactsHint': 'Defeat the era boss at each age gate to claim its artifact.',

  'err.invalid_email': 'Enter a valid email.',
  'err.send_failed': 'Could not send the code, try again shortly.',
  'err.missing_fields': 'Missing info.',
  'err.need_code': 'Request a code first.',
  'err.too_many_attempts': 'Too many attempts. Request a new code.',
  'err.code_expired': 'The code has expired.',
  'err.code_wrong': 'The code is wrong.',
  'err.invalid_save': 'Invalid save data.',
  'err.empty_feedback': 'The feedback is empty.',
  'err.auth_required': 'Sign in required.',
  'account.feedbackEmpty': 'Write something first.',
  'account.feedbackThanks': 'Thanks, sent!',
  'account.feedbackError': 'Could not send, try again.',
  'account.btnTitle': 'Account & feedback',
  'milestone.ageDawns': 'Age {n} dawns',
  'milestone.synergy': 'Synergy: {name}',
  'milestone.artifactFound': 'Artifact found: {name}',
  'milestone.artifactHint': 'Activate it with resources',
  'milestone.artifactActive': '{name} activated',
  'milestone.conquerReady': '{planet} is yours to claim',
  'milestone.conquerReadySub': 'Ascend to bank cosmic power and take the next world',
  'milestone.ascended': 'Ascended to {planet}',
  'milestone.ascendedSub': '+{gain} cosmic power',
  'combat.dodge': 'Dodge!',
  'combat.miss': 'Miss',
  'combat.block': 'Block',
  'lab.owned': 'Owned',
  'lab.activate': 'Activate',
  'ui.soundTitle': 'Sound on/off',
  'login.signedIn': 'signed in',
  'login.offline': 'offline',
  'skill.lv': 'Lv {lvl}/{max}',
  'skill.max': 'MAX',
  'offline.nothing': 'Nothing gathered while away.',
  'death.firstLede': 'This is not the end. It is the loop: every fall pays wisdom. Spend it, then rise stronger.',
  'death.spendHint': 'Spend your points, then rise again.',
  'err.google_failed': 'Google sign-in failed.',
  'login.or': 'or',

  // Wave skip + voluntary retirement (v0.10)
  'wave.sendNow': 'Send wave now',
  'retire.btn': 'End civilization',
  'retire.lockedAt': 'Retire unlocks at Age {n}',
  'retire.confirm': 'Tap again to end this run',
  'death.retireLede': 'You ended this line on your own terms: Age {age}, {waves} waves held. Spend the wisdom, then found a stronger one.',
};
